import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { fmtMY } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Sparkle, UploadSimple, FilePdf, ArrowsClockwise } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const empty = {
  ssm_no: "", name: "", incorporation_date: "", financial_year_end: "",
  address: "", registered_address: "", contact_person: "", contact_email: "", contact_phone: "",
  notes: "", directors: [], shareholders: [], secretaries: [],
};

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [rows, setRows] = useState([]);
  const [bulkRenewing, setBulkRenewing] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genYear, setGenYear] = useState(String(new Date().getFullYear()));
  const [genOverwrite, setGenOverwrite] = useState(false);
  const [generating, setGenerating] = useState(false);

  const runAutoGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/compliance/auto-generate-all", {
        financial_year: genYear.trim(),
        overwrite: genOverwrite,
      });
      toast.success(`FY ${data.financial_year}: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped`);
      setGenOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setGenerating(false); }
  };

  // Import Annual Return
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const importAR = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    setImporting(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      const token = localStorage.getItem("cosec_token");
      const res = await fetch(`${API}/clients/import-annual-return`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "Extraction failed");
      }
      const { extracted, files: fnames } = await res.json();
      setForm({ ...empty, ...extracted,
        directors: Array.isArray(extracted.directors) ? extracted.directors : [],
        shareholders: Array.isArray(extracted.shareholders) ? extracted.shareholders : [],
      });
      setOpen(true);
      toast.success(
        `${fnames.length} file(s) merged · ${extracted.directors?.length || 0} directors · ${extracted.shareholders?.length || 0} shareholders`
      );
    } catch (e) {
      toast.error(String(e.message || e).slice(0, 240));
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const load = () => api.get(`/clients${search ? `?search=${encodeURIComponent(search)}` : ""}`).then(r => setRows(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [search]);

  const renewClient = async (c) => {
    if (!window.confirm(`Renew "${c.name}" for 1 more month? This uses 1 credit.`)) return;
    try {
      const { data } = await api.post(`/clients/${c.id}/renew`);
      toast.success(`Renewed until ${fmtMY(data.active_until)}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Renewal failed");
    }
  };

  const expiringCount = rows.filter(c => {
    if (!c.active_until) return false;
    const days = Math.round((new Date(c.active_until) - new Date(new Date().toDateString())) / 86400000);
    return days <= 7;
  }).length;

  const renewAllExpiring = async () => {
    if (expiringCount === 0) return toast.info("No companies expiring this week");
    if (!window.confirm(`Renew ${expiringCount} company(s) expiring within 7 days? This will use up to ${expiringCount} credit(s).`)) return;
    setBulkRenewing(true);
    try {
      const { data } = await api.post("/clients/renew-expiring", { days_ahead: 7 });
      if (data.renewed > 0) toast.success(`Renewed ${data.renewed} company(s). ${data.skipped ? `Skipped ${data.skipped} (out of credits).` : ""}`);
      else if (data.skipped > 0) toast.error(`Skipped ${data.skipped} — not enough credits.`);
      else toast.info("Nothing to renew.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk renewal failed");
    } finally { setBulkRenewing(false); }
  };

  const submit = async () => {
    if (!form.ssm_no.trim() || !form.name.trim()) return toast.error("SSM No. and Name are required");
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Client created");
      setOpen(false); setForm(empty); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-10 max-w-[1400px]">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Directory</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight">Secretary Clients</h1>
        </div>
        <div className="flex items-center gap-2">
          {!isAdmin && expiringCount > 0 && (
            <Button variant="outline" className="rounded-sm h-10 border-[#F1C21B] text-[#8A6D00] hover:bg-[#FFF8E1]"
                    onClick={renewAllExpiring} disabled={bulkRenewing}
                    data-testid="renew-expiring-btn">
              <ArrowsClockwise size={16} weight="bold" className="mr-2" />
              {bulkRenewing ? "Renewing…" : `Renew ${expiringCount} Expiring`}
            </Button>
          )}
          <Button variant="outline" className="rounded-sm h-10"
                  onClick={() => {
                    const token = localStorage.getItem("cosec_token");
                    window.open(`${API}/clients/listing/pdf?auth=${token}`, "_blank");
                  }}
                  data-testid="client-listing-pdf-btn">
            <FilePdf size={16} weight="duotone" className="mr-2" /> Client Listing PDF
          </Button>
          <input ref={importInputRef} type="file" accept="application/pdf" hidden multiple
                 onChange={e => importAR(e.target.files)} data-testid="import-ar-input" />
          <Button variant="outline" className="rounded-sm h-10" onClick={() => importInputRef.current?.click()}
                  disabled={importing} data-testid="import-ar-btn">
            <UploadSimple size={16} weight="bold" className="mr-2" />
            {importing ? "Extracting…" : "Import AR / S14 / S17"}
          </Button>
          <Dialog open={genOpen} onOpenChange={setGenOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm h-10" data-testid="auto-generate-btn">
                <Sparkle size={16} weight="duotone" className="mr-2" /> Auto-generate Deadlines
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md rounded-sm">
              <DialogHeader><DialogTitle>Auto-generate FY deadlines</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">
                Computes AR, audit-submission and audited-report due dates for
                every client using their incorporation date and financial year end.
              </p>
              <div className="mt-4 space-y-1 font-mono text-[11px] text-muted-foreground border border-border p-3">
                <div>AR&nbsp;&nbsp;&nbsp;= Incorp. anniversary + 30 days</div>
                <div>AUDIT = FYE + 5 months</div>
                <div>FILING = FYE + 7 months</div>
              </div>
              <div className="mt-4 space-y-3">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Financial Year</Label>
                  <Input value={genYear} onChange={e => setGenYear(e.target.value)} placeholder="2025"
                         className="rounded-sm mt-1" data-testid="gen-year-input" />
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={genOverwrite} onChange={e => setGenOverwrite(e.target.checked)}
                         data-testid="gen-overwrite-toggle" />
                  <span>Overwrite existing dates for this FY</span>
                </label>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setGenOpen(false)}>Cancel</Button>
                <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={runAutoGenerate} disabled={generating}
                        data-testid="gen-run-btn">
                  {generating ? "Generating…" : "Generate"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE] h-10" data-testid="add-client-btn">
              <Plus size={16} weight="bold" className="mr-2" /> New Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-sm">
            <DialogHeader><DialogTitle>New Client</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <Field label="SSM No. *"><Input value={form.ssm_no} onChange={e => setForm({...form, ssm_no: e.target.value})} data-testid="client-ssm-input" /></Field>
              <Field label="Company Name *"><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="client-name-input" /></Field>
              <Field label="Incorporation Date (YYYY-MM-DD)"><Input value={form.incorporation_date} onChange={e => setForm({...form, incorporation_date: e.target.value})} placeholder="2020-01-15" data-testid="client-incorp-input" /></Field>
              <Field label="Financial Year End (MM-DD)"><Input value={form.financial_year_end} onChange={e => setForm({...form, financial_year_end: e.target.value})} placeholder="12-31" data-testid="client-fye-input" /></Field>
              <Field label="Contact Person"><Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} /></Field>
              <Field label="Contact Email"><Input value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} /></Field>
              <Field label="Contact Phone"><Input value={form.contact_phone} onChange={e => setForm({...form, contact_phone: e.target.value})} /></Field>
              <Field label="Business Address" full>
                <Textarea rows={2} value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
              </Field>
              <Field label="Registered Office Address" full>
                <Textarea rows={2} value={form.registered_address}
                          onChange={e => setForm({...form, registered_address: e.target.value})}
                          placeholder="As per SSM records"
                          data-testid="client-registered-address-input" />
              </Field>
              <Field label="Notes" full>
                <Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              </Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" data-testid="save-client-btn">
                {saving ? "Saving…" : "Save Client"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <div className="mb-4 relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search by name or SSM no." value={search} onChange={e => setSearch(e.target.value)}
               className="rounded-sm pl-9 h-10" data-testid="client-search-input" />
      </div>

      <div className="border border-border">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead>
            <tr className="text-left border-b border-border">
              <Th>Company</Th><Th>SSM No.</Th><Th>FYE</Th><Th>Subscription</Th><Th>Contact</Th>
              {isAdmin && <Th>Owner</Th>}
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={isAdmin ? 7 : 6} className="px-6 py-10 text-sm text-muted-foreground">No clients yet. Click <b>New Client</b> to add your first.</td></tr>
            )}
            {rows.map(c => {
              const today = new Date(); today.setHours(0,0,0,0);
              const exp = c.active_until ? new Date(c.active_until) : null;
              const daysLeft = exp ? Math.round((exp - today) / 86400000) : null;
              const state = !exp ? "none" : daysLeft < 0 ? "expired" : daysLeft <= 7 ? "warn" : "ok";
              const cls = state === "expired" ? "bg-[#FEE2E2] text-[#DA1E28] border-[#FCA5A5]"
                        : state === "warn"    ? "bg-[#FFF8E1] text-[#8A6D00] border-[#F1C21B]"
                        : "bg-[#DEFBE6] text-[#0E6027] border-[#A7F0BA]";
              return (
              <tr key={c.id} className="data-row border-b border-border">
                <td className="px-6 py-3">
                  <div className="font-medium flex items-center gap-2">
                    {c.name}
                    {c.is_shared_with_me && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm border border-[#8A6D00] text-[9px] font-mono uppercase tracking-widest text-[#8A6D00] bg-[#FFF8E1]"
                            data-testid={`shared-badge-${c.id}`}>
                        Shared
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">Incorp {fmtMY(c.incorporation_date)}</div>
                </td>
                <td className="px-6 py-3 font-mono text-xs">{c.ssm_no}</td>
                <td className="px-6 py-3 font-mono text-xs">{c.financial_year_end || "—"}</td>
                <td className="px-6 py-3">
                  {exp ? (
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex w-fit items-center px-2 py-0.5 rounded-sm border font-mono text-[10px] uppercase tracking-wider ${cls}`}>
                        {state === "expired" ? "Expired" : state === "warn" ? `${daysLeft}d left` : "Active"}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground">Until {fmtMY(c.active_until)}</span>
                    </div>
                  ) : <span className="font-mono text-[10px] text-muted-foreground">—</span>}
                </td>
                <td className="px-6 py-3 text-xs">
                  <div>{c.contact_person || "—"}</div>
                  <div className="text-muted-foreground">{c.contact_email}</div>
                </td>
                {isAdmin && (
                  <td className="px-6 py-3 text-xs" data-testid={`owner-${c.id}`}>
                    <div className="font-medium">{c.owner_name || c.owner_email || "—"}</div>
                    {c.owner_name && c.owner_email && (
                      <div className="text-muted-foreground font-mono text-[10px]">{c.owner_email}</div>
                    )}
                  </td>
                )}
                <td className="px-6 py-3 text-right">
                  <div className="inline-flex items-center gap-2">
                    {(state === "warn" || state === "expired") && (
                      <Button size="sm" variant="outline"
                              className="h-7 rounded-sm text-[11px] font-mono uppercase tracking-wider"
                              onClick={() => renewClient(c)} data-testid={`renew-client-${c.id}`}>
                        Renew (1 credit)
                      </Button>
                    )}
                    <Link to={`/clients/${c.id}`} className="text-xs font-mono uppercase tracking-widest underline"
                          data-testid={`open-client-${c.id}`}>
                      Open →
                    </Link>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const Th = ({ children, className = "" }) =>
  <th className={`px-6 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-normal ${className}`}>{children}</th>;
